import { Play, Pause, SkipBack, SkipForward, X } from "lucide-react";
import clsx from "clsx";
import { useReplayStore, SPEEDS, type Speed } from "@/store/replay";
import { Bookmarks } from "./Bookmarks";
import { formatDate } from "@/lib/format";
import type { Candle } from "@/hooks/useCandles";

interface ReplayBarProps {
  candles: Candle[];
  symbol: string;
  exchange: string;
  timeframe: string;
}

export function ReplayBar({ candles, symbol, exchange, timeframe }: ReplayBarProps) {
  const {
    active,
    setupMode,
    currentIndex,
    totalCandles,
    playing,
    speed,
    togglePlay,
    stepForward,
    stepBackward,
    setSpeed,
    exit,
  } = useReplayStore();

  if (!active) return null;

  const currentCandle = currentIndex !== null ? candles[currentIndex] : null;
  const atEnd = currentIndex !== null && currentIndex >= totalCandles - 1;
  const atStart =
    currentIndex !== null && useReplayStore.getState().startIndex !== null &&
    currentIndex <= useReplayStore.getState().startIndex!;

  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-t border-border bg-bg-panel px-4 animate-slide-up">
      {setupMode ? (
        // ─── Setup mode: instructions instead of controls ─────────────────────
        <>
          <div className="flex items-center gap-2 text-xs text-accent">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" />
            <span className="font-semibold uppercase tracking-wider">
              Replay setup
            </span>
            <span className="text-text-secondary">
              Click any candle on the chart to set replay start point
            </span>
          </div>
          <button
            onClick={exit}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
            Exit replay
            <kbd className="ml-1 rounded bg-bg-hover px-1 py-px text-2xs text-text-muted">Esc</kbd>
          </button>
        </>
      ) : (
        // ─── Active playback ──────────────────────────────────────────────────
        <>
          {/* Left — playback controls */}
          <div className="flex items-center gap-1">
            <IconButton
              icon={SkipBack}
              onClick={stepBackward}
              disabled={atStart}
              label="Step back"
              kbd="←"
            />
            <IconButton
              icon={playing ? Pause : Play}
              onClick={togglePlay}
              disabled={atEnd && !playing}
              label={playing ? "Pause" : "Play"}
              kbd="Space"
              primary
            />
            <IconButton
              icon={SkipForward}
              onClick={stepForward}
              disabled={atEnd}
              label="Step forward"
              kbd="→"
            />

            {/* Speed selector */}
            <div className="ml-3 flex items-center gap-px rounded-md border border-border bg-bg-deep p-0.5">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s as Speed)}
                  className={clsx(
                    "rounded px-2 py-1 font-mono text-2xs font-semibold transition-colors",
                    speed === s
                      ? "bg-accent text-white"
                      : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                  )}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>

          {/* Center — progress */}
          <div className="flex items-center gap-3 text-xs tabular font-mono">
            {currentCandle && (
              <>
                <span className="text-text-secondary">
                  <span className="text-text-primary">{(currentIndex ?? 0) + 1}</span>
                  <span className="text-text-muted"> / {totalCandles}</span>
                </span>
                <span className="text-text-muted">·</span>
                <span className="text-text-primary">{formatDate(currentCandle.ts)}</span>
                {atEnd && (
                  <span className="rounded bg-warn/15 px-1.5 py-px text-2xs uppercase tracking-wider text-warn">
                    end
                  </span>
                )}
              </>
            )}
          </div>

          {/* Right — bookmarks + exit */}
          <div className="flex items-center gap-1">
            <Bookmarks
              candles={candles}
              symbol={symbol}
              exchange={exchange}
              timeframe={timeframe}
            />
            <button
              onClick={exit}
              className="ml-1 flex items-center gap-1.5 rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-elevated hover:text-bear"
            >
              <X className="h-3.5 w-3.5" />
              <span>Exit</span>
              <kbd className="ml-0.5 rounded bg-bg-hover px-1 py-px text-2xs text-text-muted">
                Esc
              </kbd>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function IconButton({
  icon: Icon,
  onClick,
  disabled,
  label,
  kbd,
  primary,
}: {
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  label: string;
  kbd?: string;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={kbd ? `${label} (${kbd})` : label}
      aria-label={label}
      className={clsx(
        "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
        primary
          ? "bg-accent text-white hover:bg-accent-hover disabled:bg-bg-elevated disabled:text-text-muted"
          : "text-text-secondary hover:bg-bg-elevated hover:text-text-primary disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-secondary",
        disabled && "cursor-not-allowed"
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
